import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './TaskCard.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

interface TaskCardBase {
  /** 13/17 semibold at `--cv15-text` — `Build Header v1.5 in Storybook`, `Pushed v1.5`. */
  title: string;
  /** 11/14 regular at `--cv15-text-hint` — `24 files · started 14:02`, `commit 79dd7f29 · …`. */
  meta: string;
  theme?: ChatTheme;
  className?: string;
}

/**
 * Three kinds, and the third is a different card wearing the same box.
 *
 * `running` and `done` are the two ends of one background job (*Background task* -> *Task reports back*): same solid
 * border, same trailing status word, only the glyph and the strings change. `external` is
 * *Outside the deck* — work that left the app — and it is the one card in all 80 artboards with a dashed
 * border. Its trailing slot holds a glyph instead of a word for the same reason: there is no
 * status to report on something this thread can no longer see.
 */
type TaskCardKind =
  | {
      kind: 'running';
      /** Right-aligned, 12/18 at hint. `Running in the background`. */
      status: string;
      brand?: never;
      onOpen?: never;
    }
  | {
      kind: 'done';
      /** Right-aligned, 12/18 at hint. `Done`. */
      status: string;
      brand?: never;
      onOpen?: never;
    }
  | {
      kind: 'external';
      /** The only mark the design ships for this. Kept a union so a second one has a place to go. */
      brand: 'github';
      status?: never;
      /** Follows the effect out of the app. With a handler the whole card becomes a `<button>`. */
      onOpen?: () => void;
    };

export type TaskCardProps = TaskCardBase & TaskCardKind;

/**
 * The 52px row a background job reports through, and the dashed row a push leaves behind.
 *
 * Deliberately **not** `ArtifactCard`. Four numbers separate them and every one is stated
 * independently in the file: 52 not 56, a 17px glyph not a 44x25 plate, a status word not a
 * chevron, and a dashed `--cv15-border-strong` edge no artifact card ever wears. A shared box with
 * a `variant` prop would have to average all four.
 *
 * The dashed border is the whole point of `external` and it is doing semantic work: the *Outside the deck*
 * caption is "a push is not artifact work — you cannot undo it from here, so it looks different."
 * The card is drawn as an outline that has left the deck's material.
 */
export function TaskCard(props: TaskCardProps) {
  const { title, meta, theme: themeProp, className } = props;
  const theme = useV15Theme(themeProp);
  const external = props.kind === 'external';
  const interactive = external && props.onOpen !== undefined;

  const rootClass = cn(
    chatThemeClass(theme),
    styles.card,
    external && styles.dashed,
    interactive && styles.interactive,
    className,
  );

  const body = (
    <>
      {/* Rendered bare, with no slot around it. `ActivityGlyph` is already `flex: 0 0 auto` in a
          square box at the requested size, so a wrapper would only restate what INVENTORY §1.1
          keeps in one file. 17 is that ladder's card rung — one up from the 16px list rows,
          because a card's mark is the only glyph in its row. */}
      {props.kind === 'external' ? (
        /* Tokenized, not brand-coloured — DECISIONS §1. Paper hardcodes `#24292F`, which on
           `--cv15-ground-1` (~#141415) is about 1.1:1 and effectively invisible. Tinting it
           follows the call the design had already made for the Notion mark next door rather
           than inventing a rule for one icon. `ActivityGlyph` keeps GitHub out of its
           preserve-colours set for exactly this. */
        <ActivityGlyph tool={props.brand} size={17} theme={theme} />
      ) : (
        /* `done` maps to the lifecycle terminal `complete`, not to the list state `done`:
           `complete` is the check-in-circle at subtle, which is what *Task reports back* draws. The `done`
           state is a bare tick at muted — a row inside a list, not a card reporting back. */
        <ActivityGlyph state={props.kind === 'running' ? 'running' : 'complete'} size={17} theme={theme} />
      )}
      <span className={styles.stack}>
        <span className={styles.title}>{title}</span>
        <span className={styles.meta}>{meta}</span>
      </span>
      <span className={styles.spacer} />
      {props.kind === 'external' ? (
        /* Chrome, which is why it is neither a `tool` nor a `state`: "this opens elsewhere" is a
           fact about the card, not about the task, and putting it in the vocabulary of state was
           always the wrong shape. §1.8 specifies it at the card level — trailing
           `arrow-up-right 16 --cv15-text-hint`.

           `inherit`, because the tone is not one value: `.trailingGlyph` rests at hint and lifts
           one rung to muted under the card's pointer, a rung below `ArtifactCard`'s chevron at
           both ends. That relationship is a rule about the two cards and it stays in the CSS. */
        <span className={styles.trailingGlyph}>
          <ActivityGlyph chrome="external" size={16} tone="inherit" theme={theme} />
        </span>
      ) : (
        /* A background task changes state while the user is reading something else — that is the
           definition of the thing. So the status word is the live region, and only the word: the
           title and the meta change with it and re-announcing all three would read the whole card
           aloud on every tick. */
        <span className={styles.status} aria-live="polite">
          {props.status}
        </span>
      )}
    </>
  );

  if (interactive) {
    return (
      <button type="button" className={rootClass} onClick={props.onOpen}>
        {body}
      </button>
    );
  }

  return <div className={rootClass}>{body}</div>;
}
